import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronRight, ChevronDown, Trash, Hotel, ArrowRight, MoreVertical } from 'lucide-react';
import { AccommodationInfo } from './types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Cache global para armazenar informações de hospedagens por ID
// Isso ajuda a manter as informações entre renderizações e mudanças de página
const accommodationCache: Record<string, AccommodationInfo> = {};

// Extend AccommodationInfo to include pageName, which will be added in UnifiedItemsView
type AccommodationInfoWithPage = AccommodationInfo & { pageName?: string };

interface AccommodationEventFormProps {
  accommodationEvent: {
    id: string;
    title: string;
    accommodationId: string;
    isCheckIn: boolean;
  };
  index: number;
  pageId: string;
  accommodations: AccommodationInfoWithPage[];
  updateAccommodationEvent: (pageId: string, accommodationEventId: string, field: string, value: any) => void;
  deleteAccommodationEvent: (pageId: string, accommodationEventId: string) => void;
  moveAccommodationEvent?: (accommodationEventId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function AccommodationEventForm({
  accommodationEvent,
  index,
  pageId,
  accommodations,
  updateAccommodationEvent,
  deleteAccommodationEvent,
  moveAccommodationEvent,
  isCollapsed,
  onToggleCollapse
}: AccommodationEventFormProps) {
  console.log('[AccommodationEventForm] Recebendo props:', {
    accommodationEventId: accommodationEvent.id,
    hasMoveFunction: Boolean(moveAccommodationEvent),
    accommodationId: accommodationEvent.accommodationId,
    title: accommodationEvent.title,
    isCheckIn: accommodationEvent.isCheckIn,
    totalAccommodations: accommodations.length
  });
  
  // Find the initial accommodation state immediately on render, first from cache, then from props
  const cachedAccommodation = accommodationEvent.accommodationId ? accommodationCache[accommodationEvent.accommodationId] : null;
  const matchingAccommodation = accommodationEvent.accommodationId ? accommodations.find(a => a.id === accommodationEvent.accommodationId) : null;
  const initialAccommodation = cachedAccommodation || matchingAccommodation || null;
  
  // Estado e referências centralizadas
  const accommodationsRef = useRef(accommodations);
  const accommodationEventRef = useRef(accommodationEvent);
  const [selectedAccommodation, setSelectedAccommodation] = useState<AccommodationInfoWithPage | null>(initialAccommodation);
  const prevAccommodationIdRef = useRef<string | null>(accommodationEvent.accommodationId || null);
  const mountedRef = useRef(false);
  const updateTitleRef = useRef(false);

  // Update refs when props change
  useEffect(() => {
    accommodationsRef.current = accommodations;
    accommodationEventRef.current = accommodationEvent;
    
    // Para debug, registrar a mudança na prop
    console.log("[AccommodationEventForm] Props atualizadas:", {
      accommodationId: accommodationEvent.accommodationId, 
      prevAccommodationId: prevAccommodationIdRef.current,
      hasChanged: accommodationEvent.accommodationId !== prevAccommodationIdRef.current,
      accommodationsCount: accommodations.length,
      title: accommodationEvent.title,
      currentlySelected: selectedAccommodation ? selectedAccommodation.id : null
    });
    
    // Verificar se o accommodationId está vazio mas temos um título com o nome da hospedagem
    if (!accommodationEvent.accommodationId && accommodationEvent.title && selectedAccommodation) {
      // Se o título contém o nome da hospedagem selecionada, provavelmente estamos lidando
      // com um caso em que o accommodationId foi perdido durante uma atualização, mas as informações
      // ainda são válidas
      if (accommodationEvent.title.includes(selectedAccommodation.name)) {
        console.log('[AccommodationEventForm] Detectada perda de ID com título válido. Restaurando associação:', {
          title: accommodationEvent.title,
          accommodation: selectedAccommodation.name,
          accommodationId: selectedAccommodation.id
        });
        
        // Restaurar o accommodationId correto
        // Precisamos fazer isso assincronamente para evitar um possível loop de renderização
        setTimeout(() => {
          updateAccommodationEvent(pageId, accommodationEvent.id, 'accommodationId', selectedAccommodation.id);
        }, 10);
        
        // Manter a seleção atual
        return;
      }
    }
    
    // Se o accommodationId mudou externamente, tentar buscar a hospedagem
    if (accommodationEvent.accommodationId && accommodationEvent.accommodationId !== prevAccommodationIdRef.current) {
      console.log(`[AccommodationEventForm] accommodationId prop changed to ${accommodationEvent.accommodationId}. Searching for accommodation...`);
      
      // Primeiro buscar no cache
      let matchingAccommodation = accommodationEvent.accommodationId ? accommodationCache[accommodationEvent.accommodationId] : null;
      
      // Se não encontrou no cache, buscar na lista de hospedagens
      if (!matchingAccommodation) {
        matchingAccommodation = accommodations.find(a => a.id === accommodationEvent.accommodationId);
      }
      
      if (matchingAccommodation) {
        console.log('[AccommodationEventForm] Found matching accommodation:', {
          id: matchingAccommodation.id,
          name: matchingAccommodation.name,
          source: accommodationCache[accommodationEvent.accommodationId] ? 'cache' : 'props'
        });
        
        // Atualizar o estado e o cache
        setSelectedAccommodation(matchingAccommodation);
        accommodationCache[matchingAccommodation.id] = matchingAccommodation;
        prevAccommodationIdRef.current = accommodationEvent.accommodationId;
        
        // Verificar se precisa atualizar o título
        if (!accommodationEvent.title || accommodationEvent.title === '') {
          const eventType = accommodationEvent.isCheckIn ? 'Check-in' : 'Check-out';
          const newTitle = `${eventType} - ${matchingAccommodation.name}`;
          
          console.log('[AccommodationEventForm] Atualizando título do evento após encontrar hospedagem:', {
            oldTitle: accommodationEvent.title,
            newTitle,
            accommodationId: matchingAccommodation.id
          });
          
          updateAccommodationEvent(pageId, accommodationEvent.id, 'title', newTitle);
        }
      } else {
        console.warn('[AccommodationEventForm] Accommodation not found for ID:', accommodationEvent.accommodationId);
        // Se não encontrou, mas temos um ID, registrar o ID no cache para tentar
        // recuperar depois se a hospedagem for carregada posteriormente
        setSelectedAccommodation(null);
        prevAccommodationIdRef.current = accommodationEvent.accommodationId;
      }
    } else if (!accommodationEvent.accommodationId && prevAccommodationIdRef.current) {
      // Se o ID foi removido, verificar se o título ainda corresponde à hospedagem anterior
      // para decidir se devemos manter a seleção ou limpar
      if (selectedAccommodation && accommodationEvent.title.includes(selectedAccommodation.name)) {
        console.log('[AccommodationEventForm] accommodationId prop removed, but title matches selected accommodation. Preserving selection.');
      } else {
        // Limpar se o accommodationId foi removido externamente
        console.log('[AccommodationEventForm] accommodationId prop removed. Clearing selected accommodation.');
        setSelectedAccommodation(null);
        prevAccommodationIdRef.current = null;
      }
    }
  }, [accommodations, accommodationEvent, pageId, selectedAccommodation, updateAccommodationEvent]);
  
  // Quando selectedAccommodation muda, atualizar o cache
  useEffect(() => {
    if (selectedAccommodation) {
      console.log('[AccommodationEventForm] Updating cache for accommodation:', selectedAccommodation.id);
      accommodationCache[selectedAccommodation.id] = selectedAccommodation;
    }
  }, [selectedAccommodation]);

  // This effect handles title updates after accommodation changes
  useEffect(() => {
    // Only update title if flag is set and we have a selected accommodation
    if (!updateTitleRef.current || !selectedAccommodation) {
      return;
    }

    // If we need to update the title
    const eventType = accommodationEvent.isCheckIn ? 'Check-in' : 'Check-out';
    const newTitle = `${eventType} - ${selectedAccommodation.name}`;
    
    console.log('[AccommodationEventForm] Título atualizado devido a flag:', {
      oldTitle: accommodationEvent.title,
      newTitle,
      accommodationId: selectedAccommodation.id
    });
    
    updateAccommodationEvent(pageId, accommodationEvent.id, 'title', newTitle);
    
    // Reset the flag
    updateTitleRef.current = false;
  }, [selectedAccommodation, accommodationEvent.isCheckIn, accommodationEvent.title, pageId, updateAccommodationEvent]);

  // Centralizar lógica de atualização de título
  const updateEventTitle = useCallback((accommodation: AccommodationInfoWithPage | null, isCheckIn: boolean) => {
    if (!accommodation) return;
    
    const eventType = isCheckIn ? 'Check-in' : 'Check-out';
    const newTitle = `${eventType} - ${accommodation.name}`;
    
    console.log('[AccommodationEventForm] updateEventTitle:', {
      accommodationId: accommodation.id,
      eventId: accommodationEventRef.current.id,
      newTitle
    });
    
    updateAccommodationEvent(pageId, accommodationEventRef.current.id, 'title', newTitle);
  }, [pageId, updateAccommodationEvent]);

  // Nova versão: apenas dispara a atualização global, o estado local será sincronizado pelo efeito
  const handleAccommodationChange = (accommodationId: string) => {
    if (!accommodationId) {
      console.log('[AccommodationEventForm] Clearing selected accommodation via dropdown.');
      updateAccommodationEvent(pageId, accommodationEvent.id, 'accommodationId', '');
      return;
    }
    const accommodation = accommodationsRef.current.find(a => a.id === accommodationId);
    if (accommodation) {
      console.log('[AccommodationEventForm] Accommodation selected via dropdown:', {
        id: accommodation.id,
        name: accommodation.name,
        pageName: accommodation.pageName
      });
      updateAccommodationEvent(pageId, accommodationEvent.id, 'accommodationId', accommodation.id);
      // O título será atualizado pelo efeito de sincronização
    } else {
      console.warn('[AccommodationEventForm] Selected accommodation not found in list:', accommodationId);
      updateAccommodationEvent(pageId, accommodationEvent.id, 'accommodationId', '');
    }
  };

  const handleTitleChange = (title: string) => {
    console.log('[AccommodationEventForm] Changing title directly:', title);
    updateAccommodationEvent(pageId, accommodationEvent.id, 'title', title);
  };

  const handleIsCheckInChange = (isCheckIn: boolean) => {
    console.log('[AccommodationEventForm] Changing isCheckIn:', isCheckIn);
    
    // Primeiro atualizar o tipo do evento
    updateAccommodationEvent(pageId, accommodationEvent.id, 'isCheckIn', isCheckIn);
    
    // Se já temos uma hospedagem selecionada, atualizar o título
    if (selectedAccommodation) {
      const eventType = isCheckIn ? 'Check-in' : 'Check-out';
      const newTitle = `${eventType} - ${selectedAccommodation.name}`;
      console.log('[AccommodationEventForm] Auto-updating title due to isCheckIn change:', newTitle);
      updateAccommodationEvent(pageId, accommodationEvent.id, 'title', newTitle);
    }
  };

  // Simplified display function for accommodation details
  const getAccommodationDisplay = (accommodation: AccommodationInfoWithPage | null) => {
    if (!accommodation) return 'N/A';
    let displayText = accommodation.name || 'Hospedagem sem nome';
    if (accommodation.pageName) {
      displayText += ` (${accommodation.pageName})`;
    }
    return displayText;
  };

  // Texto do cabeçalho calculado antecipadamente
  const headingText = accommodationEvent.title || (accommodationEvent.isCheckIn ? 'Check-in' : 'Check-out');

  return (
    <Card className={cn(
      "border shadow-sm transition-all border-l-4",
      accommodationEvent.isCheckIn ? "border-l-green-500" : "border-l-red-500",
      isCollapsed && "border-transparent",
      isCollapsed ? (accommodationEvent.isCheckIn ? "border-l-green-500" : "border-l-red-500") : ""
    )}>
      <div 
        className="px-4 py-2 flex items-center justify-between bg-muted/30 cursor-pointer"
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            className="h-8 w-8 p-0" 
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse();
            }}
          >
            {isCollapsed ? 
              <ChevronRight className="h-4 w-4" /> : 
              <ChevronDown className="h-4 w-4" />
            }
          </Button>
          <Hotel className={cn("h-4 w-4", {
            "text-green-500": accommodationEvent.isCheckIn,
            "text-red-500": !accommodationEvent.isCheckIn
          })} />
          <div className="flex-grow min-w-0">
            <span className="font-medium text-sm truncate block" title={headingText}>
              {headingText}
            </span>
            {selectedAccommodation && (
              <span className="text-xs text-gray-500 truncate block" title={getAccommodationDisplay(selectedAccommodation)}>
                {getAccommodationDisplay(selectedAccommodation)}
          </span>
            )}
            {!selectedAccommodation && accommodationEvent.accommodationId && (
              <span className="text-xs text-red-500 truncate block">Hospedagem vinculada não encontrada</span>
            )}
          </div>
        </div>
        
        <div className="flex items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Abrir menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {moveAccommodationEvent !== undefined && (
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('[AccommodationEventForm] Clicou para mover:', accommodationEvent.id);
                    moveAccommodationEvent(accommodationEvent.id);
                  }}
                >
                  <ArrowRight className="h-4 w-4 mr-2" />
                  <span>Mover</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem 
                onClick={(e) => {
                  e.stopPropagation();
                  deleteAccommodationEvent(pageId, accommodationEvent.id);
                }}
                className="text-destructive"
              >
                <Trash className="h-4 w-4 mr-2" />
                <span>Remover</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {!isCollapsed && (
        <CardContent className="p-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor={`accommodation-event-title-${accommodationEvent.id}`}>Título</Label>
            <Input
              id={`accommodation-event-title-${accommodationEvent.id}`}
              value={accommodationEvent.title || ''}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Digite o título do evento"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`accommodation-select-${accommodationEvent.id}`}>Hotel</Label>
            <Select
              value={accommodationEvent.accommodationId || ''}
              onValueChange={handleAccommodationChange}
            >
              <SelectTrigger id={`accommodation-select-${accommodationEvent.id}`}>
                <SelectValue placeholder="Selecione um hotel" />
              </SelectTrigger>
              <SelectContent>
                {accommodations.map((accommodation) => (
                  <SelectItem key={accommodation.id} value={accommodation.id}>
                    {accommodation.name}
                    {accommodation.pageName && <span className="text-xs text-gray-500 ml-1">({accommodation.pageName})</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id={`isCheckIn-${accommodationEvent.id}`}
              checked={accommodationEvent.isCheckIn}
              onCheckedChange={handleIsCheckInChange}
            />
            <Label htmlFor={`isCheckIn-${accommodationEvent.id}`}>
              {accommodationEvent.isCheckIn ? 'Check-in' : 'Check-out'}
            </Label>
          </div>

          {selectedAccommodation && (
            <div className="space-y-2">
              <Label>Informações do Hotel</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Localização</Label>
                  <p className="text-sm">{selectedAccommodation.location || 'Não informado'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Check-in</Label>
                  <p className="text-sm">
                    {selectedAccommodation.checkInDate ? new Date(selectedAccommodation.checkInDate).toLocaleDateString() : 'Não informado'}
                    {selectedAccommodation.checkInTime && ` às ${selectedAccommodation.checkInTime}`}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Check-out</Label>
                  <p className="text-sm">
                    {selectedAccommodation.checkOutDate ? new Date(selectedAccommodation.checkOutDate).toLocaleDateString() : 'Não informado'}
                    {selectedAccommodation.checkOutTime && ` às ${selectedAccommodation.checkOutTime}`}
                  </p>
                </div>
                {/* Exibir página da hospedagem */}
                {(selectedAccommodation as AccommodationInfoWithPage).pageName && (
                  <div>
                    <Label className="text-xs text-gray-500">Página</Label>
                    <p className="text-sm">{(selectedAccommodation as AccommodationInfoWithPage).pageName}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
} 